import { browser } from '$app/environment';
import { writable } from 'svelte/store';
import Cookies from 'js-cookie';

export interface AccessibilityPreferences {
  reduceColor: boolean;
  reduceMotion: boolean;
}

type PreferenceCheckFn = () => boolean | null;

enum CookieNames {
  ReduceColor = 'PREFERS_REDUCE_COLOR',
  ReduceMotion = 'PREFERS_REDUCE_MOTION'
}

const fromPreferenceCookie = (cookieName: string): PreferenceCheckFn => {
  return () => {
    const value = Cookies.get(cookieName);

    if (!value) {
      return null;
    }

    return value === 'true';
  };
};

const fromMediaPreference = (name: string, positiveValue: string) => {
  return () => {
    if (!browser) {
      return null;
    }

    const { matches } = window.matchMedia(`--${name}: ${positiveValue}`);

    return matches;
  };
};

const valueFrom = (...checkFns: PreferenceCheckFn[]): boolean => {
  for (const fn of checkFns) {
    const val = fn();

    if (val !== null) {
      return val;
    }
  }

  return false;
};

const createStore = () => {
  const { subscribe, update } = writable<AccessibilityPreferences>({
    reduceMotion: valueFrom(
      fromPreferenceCookie(CookieNames.ReduceMotion),
      fromMediaPreference('prefers-reduce-motion', 'reduce')
    ),
    reduceColor: valueFrom(fromPreferenceCookie(CookieNames.ReduceColor))
  });

  const setPreference = (name: string, value: boolean) => {
    const cookieName = name === 'reduceMotion' ? CookieNames.ReduceMotion : CookieNames.ReduceColor;

    Cookies.set(cookieName, String(value));
    update((state) => ({ ...state, [name]: value }));
  };

  return {
    subscribe,
    setReduceColor: (value: boolean) => setPreference('reduceColor', value),
    setReduceMotion: (value: boolean) => setPreference('reduceMotion', value)
  };
};

export const accessibilityPreferences = createStore();
